/**
 * "Is the child there?" — the first of the six core motion inputs (MVP notes §17).
 *
 * It is the only detector that speaks about the body as a whole, so it is the one whose `lost`
 * ends the session's belief that anybody is in front of the camera. Everything else in the adapter
 * hangs off that: no zone, no jump and no posture may be believed while nobody is there.
 */

import { CORE_POSE_LANDMARKS } from '../pose-types';
import type { DetectorOptions, DetectorOutcome, PoseDetector } from '../detector-types';
import { createTrackingGate } from './tracking-gate';

export function createPresenceDetector(options: DetectorOptions = {}): PoseDetector {
  const gate = createTrackingGate({
    landmarks: CORE_POSE_LANDMARKS,
    tunables: options.tunables,
  });
  let present = false;

  return {
    capability: 'presence',

    observe(frame): DetectorOutcome {
      const reading = gate.read(frame);

      if (reading.kind === 'blocked') {
        // Only a reported loss changes what we believe: a dip in confidence is a bad look at a
        // child who is still standing there, not a child who has gone.
        if (reading.lost) present = false;
        return reading.outcome;
      }

      if (present) return { kind: 'none' };

      present = true;
      return {
        kind: 'event',
        event: { type: 'PLAYER_ENTERED_FRAME' },
        confidence: reading.confidence,
      };
    },

    /**
     * A hole in the observation changes nothing here, and that is a decision (S5-3).
     *
     * Presence is not a claim a gap can falsify: whether somebody is in the picture is re-read from
     * the very frame that comes back, and how long it has been since anybody was is the gate's loss
     * clock — the one thing an unobserved interval must not restart, or a child who left before the
     * hole would never be reported gone.
     */
    reacquireSilently(): void {
      // Deliberately nothing.
    },

    reset(): void {
      gate.reset();
      present = false;
    },
  };
}
